import {
  WorkflowActionEnum,
  WorkflowTargetEnum,
  WorkflowWhenEnum,
  WorkflowWhenContextEnum,
  WorkflowTypeEnum,
  WorkflowActiveActionEnum,
} from 'constants-workflows';
import { Workflow } from 'application-types';
import { EVENT_KEYS } from 'constants-events';
import { constructWorkflow } from 'lane-shared/helpers/workflows/constructWorkflow';
import {
  InputCreateReminderRule,
  InputUpdateReminderRule,
  ReminderRule,
} from 'graphql-query-contracts';

const STRING_TO_WORKFLOW_ACTION_MAP: Record<string, WorkflowActionEnum> = {
  [WorkflowActionEnum.Email]: WorkflowActionEnum.Email,
  [WorkflowActionEnum.Notification]: WorkflowActionEnum.Notification,
  [WorkflowActionEnum.NotificationAndEmail]:
    WorkflowActionEnum.NotificationAndEmail,
};

const STRING_TO_WORKFLOW_TARGET_MAP: Record<string, WorkflowTargetEnum> = {
  [WorkflowTargetEnum.GroupRole]: WorkflowTargetEnum.GroupRole,
  [WorkflowTargetEnum.WorkOrderAdmin]: WorkflowTargetEnum.WorkOrderAdmin,
  [WorkflowTargetEnum.WorkOrderTeam]: WorkflowTargetEnum.WorkOrderTeam,
};

const STRING_TO_WORKFLOW_WHEN_MAP: Record<string, WorkflowWhenEnum> = {
  [WorkflowWhenEnum.Immediate]: WorkflowWhenEnum.Immediate,
  [WorkflowWhenEnum.Before]: WorkflowWhenEnum.Before,
  [WorkflowWhenEnum.After]: WorkflowWhenEnum.After,
};

export function parseWorkflowAction(
  value: string | null | undefined,
  defaultValue: WorkflowActionEnum = WorkflowActionEnum.Email
): WorkflowActionEnum {
  if (!value) {
    return defaultValue;
  }

  return STRING_TO_WORKFLOW_ACTION_MAP[value] ?? defaultValue;
}

export function parseWorkflowTarget(
  value: string | null | undefined,
  defaultValue: WorkflowTargetEnum = WorkflowTargetEnum.WorkOrderAdmin
): WorkflowTargetEnum {
  if (!value) {
    return defaultValue;
  }

  return STRING_TO_WORKFLOW_TARGET_MAP[value] ?? defaultValue;
}

export function parseWorkflowWhen(
  value: string | null | undefined,
  defaultValue: WorkflowWhenEnum = WorkflowWhenEnum.After
): WorkflowWhenEnum {
  if (!value) {
    return defaultValue;
  }

  return STRING_TO_WORKFLOW_WHEN_MAP[value] ?? defaultValue;
}

export function overrideWorkflowFields(
  workflow: Workflow,
  reminderRule: ReminderRule
): Workflow {
  return {
    ...workflow,
    _id: reminderRule.id || workflow._id,
    _created: reminderRule.createdAt
      ? new Date(reminderRule.createdAt)
      : workflow._created,
    _updated: reminderRule.updatedAt
      ? new Date(reminderRule.updatedAt)
      : workflow._updated,
    _createdBy: reminderRule.createdBy || workflow._createdBy,
    _updatedBy:
      reminderRule.updatedBy || reminderRule.createdBy || workflow._updatedBy,
    groupRole: reminderRule.teamId ? { _id: reminderRule.teamId } : undefined,
  };
}

export function convertReminderRuleToWorkflow(
  reminderRule: ReminderRule,
  channelId: string
): Workflow {
  const workflow = constructWorkflow({
    when: parseWorkflowWhen(reminderRule.triggerTiming),
    whenContext: WorkflowWhenContextEnum.Reminder,
    action: parseWorkflowAction(reminderRule.action),
    target: parseWorkflowTarget(reminderRule.target),
    event: EVENT_KEYS.EVENT_SERVICE_REQUEST_DUE,
    type: WorkflowTypeEnum.ServiceRequestReminder,
    channelId: channelId || '',
    order: reminderRule.order || 0,
    userId: reminderRule.createdBy || '',
    inStatus: reminderRule.triggerStatuses || null,
    name: reminderRule.name || '',
    time: reminderRule.triggerTime ?? undefined,
  });

  return overrideWorkflowFields(workflow, reminderRule);
}

export function convertWorkflowToCreateReminderRuleInput(
  workflow: Workflow,
  channelId?: string
): InputCreateReminderRule {
  return {
    action: workflow.action,
    target: workflow.target,
    order: workflow._order,
    name: workflow.name,
    triggerTiming: workflow.when,
    triggerTime: Math.abs(workflow.time ?? 0),
    triggerStatuses: WorkflowActiveActionEnum,
    groupId: channelId,
    teamId:
      (workflow.groupRole && typeof workflow.groupRole === 'object'
        ? workflow.groupRole._id
        : workflow.groupRole) ?? undefined,
  };
}

export function convertWorkflowToUpdateReminderRuleInput(
  workflow: Workflow
): InputUpdateReminderRule {
  return {
    id: workflow._id,
    action: workflow.action,
    target: workflow.target,
    order: workflow._order,
    name: workflow.name,
    triggerTiming: workflow.when,
    triggerTime: Math.abs(workflow.time ?? 0),
    triggerStatuses: WorkflowActiveActionEnum,
    teamId:
      (workflow.groupRole && typeof workflow.groupRole === 'object'
        ? workflow.groupRole._id
        : workflow.groupRole) ?? undefined,
  };
}
